import { useRecentPatients } from '@/hooks/useRecentPatients';
import { CarePlanView } from '@/pages/CarePlanView';
import { createFileRoute, Outlet, retainSearchParams } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

// Chat panel state lives in search params so conversations are deep-linkable
// (?chat=<mode> opens that mode's conversation list, &conversation=<id> opens that
// conversation; a bare conversation param means a patient thread, for older links)
// and the panel survives switching between the care-plan tabs.
//
// The shared drawer slot is deep-linkable the same way: ?drawerResource=<kind>&drawerResourceId=<id>
// opens that record in the drawer. These live on the layout route (not the `data` leaf) because the
// drawer is owned by `CarePlanView`, which wraps every tab, so every tab must be able to carry them.
// Deliberately distinct from the Data tab's own `resource`/`id` params, so the two surfaces can show
// different records at once. Values mirror the Data tab's plural resource vocabulary.
const CarePlanSearchSchema = z.object({
  chat: z.enum(['patient', 'research', 'worklist']).optional(),
  conversation: z.string().optional(),
  /** One-shot anchor: the message within `conversation` the thread jumps to and highlights on open.
   *  Cleared once the jump resolves, and deliberately absent from `retainSearchParams` below — a
   *  retained anchor would re-scroll the thread on every later navigation. */
  message: z.string().optional(),
  /** The care-team task open in the Worklist tab, so it survives a reload and can be shared —
   *  the task equivalent of `conversation`. */
  task: z.string().optional(),
  drawerResource: z.enum(['encounters', 'labOrders']).optional(),
  // Accepts a number as well as a string: the router JSON-parses search values, so an all-digits id
  // (`&drawerResourceId=1`) arrives as a number and a bare `z.string()` rejects it — throwing the whole
  // route rather than ignoring one param. Real ids are UUIDs, which parse as strings and round-trip
  // verbatim. An all-digits id re-serializes quoted ("1"), and one longer than ~16 digits loses
  // precision in the router's parse before any schema sees it — unfixable here, and moot for UUIDs.
  drawerResourceId: z
    .union([z.string(), z.number()])
    .transform((value) => String(value))
    .optional(),
});

const RouteComponent = (): React.ReactNode => {
  // oxlint-disable-next-line no-use-before-define
  const { patientId } = Route.useParams();
  const { addRecentPatient } = useRecentPatients();

  useEffect(() => {
    addRecentPatient(patientId);
  }, [addRecentPatient, patientId]);

  // Key on patientId so switching patients (e.g. Cmd-K) fully remounts the view instead of reusing the
  // instance with a new prop. A reused instance kept in-component state — an open encounter/fax drawer —
  // from the prior patient, rendering their note header against the new chart (a PHI leak, #6536).
  return (
    <CarePlanView key={patientId} patientId={patientId}>
      <Outlet />
    </CarePlanView>
  );
};

export const Route = createFileRoute('/_auth/care-plan/$patientId')({
  component: RouteComponent,
  loader: () => ({ crumb: 'Care Plan' }),
  validateSearch: CarePlanSearchSchema,
  // Retained so an open drawer (and the chat panel) survives navigation within the patient — tab
  // changes, and the Data tab's own navigations, which build their `search` from scratch rather than
  // spreading the previous value. Retention has to live here rather than at the call sites: any
  // navigation that doesn't spread `prev` would otherwise silently drop the drawer.
  //
  // Retention matches on key name alone, so it would also carry a record id across a patient switch,
  // rendering one patient's encounter against another's chart (the #6536 leak shape). A search
  // middleware can't tell the two cases apart — it never sees params. Two things close that instead:
  // the cross-patient jump sites pass `CLEARED_CARE_PLAN_PATIENT_SEARCH` (see `utils/carePlanNavigation`),
  // and `DrawerRecordOwnershipGate` refuses to render a record whose subject isn't the current patient,
  // covering a jump that forgets to clear as well as a hand-edited URL.
  search: {
    middlewares: [retainSearchParams(['chat', 'conversation', 'task', 'drawerResource', 'drawerResourceId'])],
  },
});
